import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useNavigate } from 'react-router-dom';
import { Check, Mail, Rocket, AlertTriangle, Copy, Eye, EyeOff } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { redirectToChatApp } from '@/utils/redirectUtils';
import { toast } from 'sonner';
import { signUpUser } from '@/services/authService';
import { Checkbox } from '@/components/ui/checkbox';
import { Link } from 'react-router-dom';

interface ThankYouContentCardProps {
  planName: string;
  planData?: any;
  email: string;
  orderId: string;
  isDirect?: boolean;
}

const ThankYouContentCard: React.FC<ThankYouContentCardProps> = ({
  planName,
  planData,
  email,
  orderId,
  isDirect = false
}) => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  const tempPassword = `${orderId}-${email.split('@')[0]}`;
  
  const handleCreateAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!agreed) {
      toast.error("Please agree to the terms and privacy policy");
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters long");
      return;
    }
    
    setIsLoading(true);
    
    try {
      const { data, error } = await signUpUser(email, password, { name });
      
      if (error) {
        console.error("Error creating account:", error);
        toast.error(error.message);
        return;
      }
      
      console.log("Account created successfully:", data);
      toast.success("Account created successfully!");
      
      // Track CompleteRegistration event when account is created
      if (typeof fbq !== 'undefined') {
        console.log('Tracking CompleteRegistration event on account creation');
        fbq('track', 'CompleteRegistration');
      } else {
        console.warn('Facebook Pixel not available for account creation tracking');
      }
      
      // Redirect to chat app after successful signup
      await redirectToChatApp(email, password);
    } catch (error) {
      console.error("Error in account creation:", error);
      toast.error("Failed to create account. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleCopyPassword = () => {
    navigator.clipboard.writeText(tempPassword);
    toast.success('Temporary password copied!');
  };
  
  const uniqueFeatures = planData?.features.filter((f: any) => f.included).map((f: any) => f.name) || [];
  
  return (
    <Card className="max-w-2xl w-full shadow-lg">
      <CardHeader className="text-center pb-4">
        {isDirect ? (
          <Badge className="mb-2 self-center bg-yellow-600 hover:bg-yellow-700">Demo Mode</Badge>
        ) : (
          <Badge className="mb-2 self-center bg-green-600 hover:bg-green-700">Payment Confirmed</Badge>
        )}
        <CardTitle className="text-2xl font-bold">Thank You for Your Order!</CardTitle>
        <CardDescription className="text-lg">
          Create your account to start using {planName}
        </CardDescription>
      </CardHeader>
      
      <form onSubmit={handleCreateAccount}>
        <CardContent className="space-y-6">
          {isDirect && (
            <div className="rounded-lg bg-yellow-50 p-4 border border-yellow-100">
              <div className="flex">
                <div className="flex-shrink-0">
                  <AlertTriangle className="h-5 w-5 text-yellow-400" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800">Demo Mode Active</h3>
                  <div className="mt-2 text-sm text-yellow-700">
                    <p>Your account has been provisioned in demo mode as the payment gateway requires configuration.</p>
                    <p className="mt-1">In production, full payment integration would be required before subscription activation.</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        
          <div className="space-y-4">
            <div className="flex justify-between border-b pb-2">
              <span className="font-medium text-gray-500">Order Reference:</span>
              <span className="font-mono">{orderId}</span>
            </div>
            
            <div className="flex justify-between border-b pb-2">
              <span className="font-medium text-gray-500">Plan:</span>
              <span className="font-medium">{planName}</span>
            </div>
            
            {planData?.monthlyAmount && (
              <div className="flex justify-between border-b pb-2">
                <span className="font-medium text-gray-500">Amount:</span>
                <span className="font-medium">₹{planData.monthlyAmount.toFixed(2)}/month</span>
              </div>
            )}
            
            <div className="flex justify-between border-b pb-2">
              <span className="font-medium text-gray-500">Email:</span>
              <span>{email}</span>
            </div>
          </div>
          
          {planData && (
            <div className="rounded-lg bg-blue-50 p-4 border border-blue-100 space-y-3">
              <h3 className="font-semibold text-blue-800 flex items-center">
                <Rocket className="h-5 w-5 mr-2" />
                Your {planData.title} Plan Benefits
              </h3>
              <ul className="space-y-1">
                {uniqueFeatures.map((feature: string, index: number) => (
                  <li key={index} className="text-sm flex items-start">
                    <Check className="h-4 w-4 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
                {planData.computeCredits && (
                  <li className="text-sm flex items-start">
                    <Check className="h-4 w-4 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span><strong>{planData.computeCredits.toLocaleString()}</strong> compute credits per month</span>
                  </li>
                )}
              </ul>
            </div>
          )}
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                placeholder="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                readOnly
                className="bg-gray-100"
              />
              <p className="text-xs text-muted-foreground">
                Email from your purchase
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Create a password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-2.5 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              <p className="text-xs text-muted-foreground">
                Password must be at least 8 characters long
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <Input
                id="confirmPassword"
                type={showPassword ? "text" : "password"}
                placeholder="Confirm your password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={8}
              />
            </div>
            
            <div className="flex items-center space-x-2">
              <Checkbox
                id="terms"
                checked={agreed}
                onCheckedChange={(checked) => setAgreed(checked as boolean)}
              />
              <label
                htmlFor="terms"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                I agree to the{" "}
                <Link to="/terms" className="text-krix-purple hover:underline">
                  Terms of Service
                </Link>{" "}
                and{" "}
                <Link to="/privacy" className="text-krix-purple hover:underline">
                  Privacy Policy
                </Link>
              </label>
            </div>
          </div>
        </CardContent>
        
        <CardFooter className="flex flex-col space-y-4">
          <Button 
            type="submit"
            className="w-full"
            disabled={isLoading}
          >
            {isLoading ? "Creating Account..." : "Create Account & Continue"}
          </Button>
          
          <p className="text-xs text-center text-gray-500">
            Already have an account?{" "}
            <Link to="/login" className="text-krix-purple hover:underline">
              Log in
            </Link>
          </p>
        </CardFooter>
      </form>
    </Card>
  );
};

export default ThankYouContentCard;
